import {FC, useMemo, useRef} from 'react';
import {SchemeHive} from '../lib/useApiaryScheme';
import ReactDOM from 'react-dom';
import {
  ApiarySchemeModalDeviceTag,
  ApiarySchemeModalDeviceTagWrapper,
  ApiarySchemeModalDrawer,
  ApiarySchemeModalHead,
  ApiarySchemeModalImage,
  ApiarySchemeModalInfoContent,
  ApiarySchemeModalInfoWrapper,
  ApiarySchemeModalItem,
  ApiarySchemeModalLink,
  ApiarySchemeModalPopup,
  ApiarySchemeModalRow,
  ApiarySchemeModalSubtitle,
  ApiarySchemeModalTag,
  ApiarySchemeModalTitle,
  ApiarySchemeModalWrapper,
  ApiarySchemeShareActions,
  ApiarySchemeShareLink,
} from './styles';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import {CloseIcon} from 'shared/ui/icons';
import {useResize} from 'app/lib/hooks/useResize.ts';

import beeImg from 'shared/assets/img/bee.png';
import mark0 from 'shared/assets/img/marks/0.png';
import mark1 from 'shared/assets/img/marks/1.png';
import mark2 from 'shared/assets/img/marks/2.png';
import mark3 from 'shared/assets/img/marks/3.png';
import mark4 from 'shared/assets/img/marks/4.png';
import {COLORS} from 'shared/consts';
import {useOutClick} from 'app/lib/hooks/useOutClick';
import {Drawer} from 'shared/ui/drawer';
import {useHivesStore} from 'entities/hives';
import {useApiariesStore} from 'entities/apiaries';
import {toast} from 'react-toastify';
import {SharedHive} from 'entities/hives/model/types';
import {useUserStore} from 'entities/user';

type ApiarySchemeModalProps = {
  isOpen: boolean;
  activeSchemeItem: SchemeHive | null;
  activeSchemeItemCoords: {top: number; left: number};
  onClose: () => void;
};
export const ApiarySchemeModal: FC<ApiarySchemeModalProps> = ({
  isOpen,
  activeSchemeItem,
  activeSchemeItemCoords,
  onClose,
}) => {
  const sharedHives = useHivesStore((s) => s.sharedHives);
  const shareHive = useHivesStore((s) => s.shareHive);
  const deShareHive = useHivesStore((s) => s.deShareHive);
  const currentApiaryId = useApiariesStore((s) => s.currentApiaryId);
  const userInfo = useUserStore((s) => s.userInfo);

  const popupRef = useRef<HTMLDivElement | null>(null);
  const {isScreenLg} = useResize();

  const markSrc = useMemo(() => {
    switch (activeSchemeItem?.color.id) {
      case 0:
        return mark0;
        break;
      case 1:
        return mark1;
        break;
      case 2:
        return mark2;
        break;
      case 3:
        return mark3;
        break;
      case 4:
        return mark4;
        break;
      default:
        return mark0;
        break;
    }
  }, [activeSchemeItem]);

  const hiveId = useMemo(() => {
    return activeSchemeItem?.id.replace('hive_', '');
  }, [activeSchemeItem]);

  const topMemoized = useMemo(() => {
    if (!popupRef.current) return 0;

    const top = activeSchemeItemCoords.top;
    const {height} = popupRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    let topAnswer = top;

    while (windowHeight - (height + topAnswer) < 20) {
      topAnswer--;
    }

    return topAnswer;
  }, [activeSchemeItem, popupRef, isOpen]);

  const leftMemoized = useMemo(() => {
    if (!popupRef.current) return 0;

    const left = activeSchemeItemCoords.left;
    const {width} = popupRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;

    let leftAnswer = left;

    while (windowWidth - (width + leftAnswer) < 20) {
      leftAnswer--;
    }

    return leftAnswer;
  }, [activeSchemeItem, popupRef, isOpen]);

  useOutClick(popupRef, onClose);

  const findedShared: SharedHive | null = useMemo(() => {
    if (!Array.isArray(sharedHives)) return null;

    const finded = sharedHives.find((el) => el.ecppHiveId === activeSchemeItem?.id);
    if (finded) return finded;

    return null;
  }, [sharedHives, activeSchemeItem]);

  const onShareClick = async () => {
    if (!activeSchemeItem || !currentApiaryId) return;

    const isShared = await shareHive({
      ecppHiveId: activeSchemeItem?.id,
      season: 2026, // todo заменить на селект сезонов
      apiaryId: currentApiaryId,
    });

    if (isShared) toast.success('Теперь улей сдаётся в аренду');
  };

  const onDeShareClick = async () => {
    if (!userInfo || !currentApiaryId || !findedShared) return;

    const isDeShared = await deShareHive(findedShared?.id);
    if (isDeShared) toast.success('Улей больше не сдаётся в аренду');
  };

  const Content = () => (
    <>
      <ApiarySchemeModalHead>
        <ApiarySchemeModalTitle>
          Информация об Улье №{activeSchemeItem?.number}
        </ApiarySchemeModalTitle>
        <Button
          iconPrefix={<CloseIcon />}
          size="s"
          colorScheme="neutral"
          variant="secondary"
          onClick={onClose}
        />
        <ApiarySchemeShareActions>
          {findedShared ? (
            <>
              <ApiarySchemeModalTag variant={findedShared.reservationId ? 'active' : 'default'}>
                {findedShared.reservationId ? 'Арендован' : 'Аренда'}
              </ApiarySchemeModalTag>
              {!findedShared.reservationId && (
                <ApiarySchemeShareLink onClick={onDeShareClick}>
                  <span>Отменить аренду</span>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7 5.5H11V4H7C5.34315 4 4 5.34315 4 7V17C4 18.6569 5.34315 20 7 20H17C18.6569 20 20 18.6569 20 17V13H18.5V17C18.5 17.8284 17.8284 18.5 17 18.5H7C6.17157 18.5 5.5 17.8284 5.5 17V7C5.5 6.17157 6.17157 5.5 7 5.5ZM15 5.5L17.4394 5.5L8.96967 13.9697L10.0303 15.0303L18.5 6.56072V9.00001H20V4.75C20 4.33579 19.6642 4.00001 19.25 4L15 4L15 5.5Z"
                      fill={COLORS.NEUTRAL.DEFAULT}
                    />
                  </svg>
                </ApiarySchemeShareLink>
              )}
            </>
          ) : (
            <ApiarySchemeShareLink onClick={onShareClick}>
              <span>Сдать в аренду</span>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7 5.5H11V4H7C5.34315 4 4 5.34315 4 7V17C4 18.6569 5.34315 20 7 20H17C18.6569 20 20 18.6569 20 17V13H18.5V17C18.5 17.8284 17.8284 18.5 17 18.5H7C6.17157 18.5 5.5 17.8284 5.5 17V7C5.5 6.17157 6.17157 5.5 7 5.5ZM15 5.5L17.4394 5.5L8.96967 13.9697L10.0303 15.0303L18.5 6.56072V9.00001H20V4.75C20 4.33579 19.6642 4.00001 19.25 4L15 4L15 5.5Z"
                  fill={COLORS.NEUTRAL.DEFAULT}
                />
              </svg>
            </ApiarySchemeShareLink>
          )}
        </ApiarySchemeShareActions>
      </ApiarySchemeModalHead>
      <ApiarySchemeModalItem>
        <ApiarySchemeModalInfoWrapper>
          <ApiarySchemeModalInfoContent>
            {Boolean(activeSchemeItem?.year) && (
              <ApiarySchemeModalRow>
                <span>Год матки</span>
                <p>{activeSchemeItem?.year}</p>
              </ApiarySchemeModalRow>
            )}
            {Boolean(activeSchemeItem?.mark) && (
              <ApiarySchemeModalRow>
                <span>Номер метки</span>
                <p>{activeSchemeItem?.mark}</p>
              </ApiarySchemeModalRow>
            )}
            {Boolean(activeSchemeItem?.breed) && (
              <ApiarySchemeModalRow>
                <span>Порода</span>
                <p>{activeSchemeItem?.breed}</p>
              </ApiarySchemeModalRow>
            )}
          </ApiarySchemeModalInfoContent>
          <ApiarySchemeModalImage>
            <img src={beeImg} alt="beeImg" />
            {Boolean(activeSchemeItem?.mark) && (
              <p>
                <img src={markSrc} alt="markSrc" />
                <span>{activeSchemeItem?.mark}</span>
              </p>
            )}
          </ApiarySchemeModalImage>
        </ApiarySchemeModalInfoWrapper>
        {(activeSchemeItem?.devicesCount || 0) <= 0 && (
          <ApiarySchemeModalLink to={`/hive/${hiveId}`}>
            <span>К улью</span>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.5577 11.4693L10.0577 5.99982L8.99996 7.06343L13.9652 12.0011L8.99996 16.9387L10.0577 18.0023L15.5577 12.5329C15.6992 12.3921 15.7788 12.2007 15.7788 12.0011C15.7788 11.8014 15.6992 11.61 15.5577 11.4693Z"
                fill={COLORS.ACCENT.DEFAULT}
              />
            </svg>
          </ApiarySchemeModalLink>
        )}
      </ApiarySchemeModalItem>

      {(activeSchemeItem?.devicesCount || 0) > 0 && (
        <ApiarySchemeModalItem>
          <ApiarySchemeModalSubtitle>Подключенные устройства</ApiarySchemeModalSubtitle>
          <ApiarySchemeModalDeviceTagWrapper>
            <ApiarySchemeModalDeviceTag>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5 3.75H15C15.6904 3.75 16.25 4.30964 16.25 5V15C16.25 15.6904 15.6904 16.25 15 16.25H5C4.30964 16.25 3.75 15.6904 3.75 15V5C3.75 4.30964 4.30964 3.75 5 3.75ZM2.5 5C2.5 3.61929 3.61929 2.5 5 2.5H15C16.3807 2.5 17.5 3.61929 17.5 5V15C17.5 16.3807 16.3807 17.5 15 17.5H5C3.61929 17.5 2.5 16.3807 2.5 15V5ZM10.8333 12.5C10.8333 12.9602 10.4602 13.3333 10 13.3333C9.53976 13.3333 9.16667 12.9602 9.16667 12.5C9.16667 12.0398 9.53976 11.6667 10 11.6667C10.4602 11.6667 10.8333 12.0398 10.8333 12.5ZM12.0833 12.5C12.0833 13.6506 11.1506 14.5833 10 14.5833C8.84941 14.5833 7.91667 13.6506 7.91667 12.5C7.91667 11.3494 8.84941 10.4167 10 10.4167C11.1506 10.4167 12.0833 11.3494 12.0833 12.5Z"
                  fill={COLORS.NEUTRAL.DEFAULT}
                />
              </svg>
              <span>Мультидатчик</span>
            </ApiarySchemeModalDeviceTag>
            <ApiarySchemeModalDeviceTag>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.25 4.79167C16.25 4.21637 15.7836 3.75 15.2083 3.75H4.79167C4.21637 3.75 3.75 4.21637 3.75 4.79167V14.375C3.75 15.4105 4.58947 16.25 5.625 16.25H14.375C15.4105 16.25 16.25 15.4105 16.25 14.375V4.79167ZM17.5 14.375C17.5 16.1009 16.1009 17.5 14.375 17.5H5.625C3.89911 17.5 2.5 16.1009 2.5 14.375V4.79167C2.5 3.52601 3.52601 2.5 4.79167 2.5H15.2083C16.474 2.5 17.5 3.52601 17.5 4.79167V14.375Z"
                  fill={COLORS.NEUTRAL.DEFAULT}
                />
                <path
                  d="M10 5C11.2848 5 12.5176 5.22837 13.6593 5.64718C14.0437 5.78821 14.2313 6.21403 14.0894 6.58895L12.9826 9.51233C12.8391 9.89131 12.4205 10.0735 12.0512 9.94019C11.5506 9.75953 11.019 9.64316 10.4661 9.60103C10.3124 9.58932 10.1569 9.58333 10 9.58333C9.27879 9.58333 8.5884 9.70937 7.94881 9.94019C7.57951 10.0735 7.16088 9.89127 7.01742 9.51233L5.91064 6.58895C5.76873 6.21403 5.95628 5.78821 6.34074 5.64718L6.55579 5.57088C7.63665 5.2007 8.7955 5 10 5ZM10 6.25C9.05012 6.25 8.13427 6.39142 7.27152 6.65344L8.01168 8.60819C8.62483 8.43476 9.27027 8.33965 9.93632 8.33394L10.0348 7.05953L11.2811 7.15576L11.1827 8.42875C11.4566 8.47346 11.7254 8.53363 11.9883 8.60799L12.7283 6.65344C11.8656 6.39146 10.9498 6.25 10 6.25Z"
                  fill={COLORS.NEUTRAL.DEFAULT}
                />
              </svg>
              <span>Весы</span>
            </ApiarySchemeModalDeviceTag>
            <ApiarySchemeModalDeviceTag>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.26163 10.7458L10.6199 16.2089C10.6515 16.3334 10.7217 16.4447 10.8204 16.5267C10.9192 16.6088 11.0414 16.6573 11.1695 16.6654C11.2976 16.6735 11.4249 16.6407 11.5332 16.5718C11.6415 16.5028 11.7251 16.4013 11.7721 16.2817L16.6232 4.16589C16.6673 4.05558 16.6781 3.93474 16.6542 3.81834C16.6304 3.70195 16.5729 3.59512 16.489 3.5111C16.405 3.42708 16.2983 3.36957 16.182 3.3457C16.0658 3.32182 15.9451 3.33263 15.8349 3.37678L3.71781 8.21912C3.59837 8.26612 3.4969 8.34984 3.42801 8.45823C3.35912 8.56662 3.32637 8.6941 3.33446 8.82231C3.34255 8.95053 3.39107 9.07286 3.47303 9.17172C3.55499 9.27057 3.66618 9.34085 3.79058 9.37243L9.26163 10.7458ZM6.02295 8.64404L9.56597 9.53343L10.2937 9.7161L10.4747 10.4442L11.3513 13.9698L14.9049 5.09456L6.02295 8.64404Z"
                  fill={COLORS.NEUTRAL.DEFAULT}
                />
              </svg>
              <span>Датчик открытия крышки с GPS</span>
            </ApiarySchemeModalDeviceTag>
          </ApiarySchemeModalDeviceTagWrapper>
          <ApiarySchemeModalLink to={`/hive/${hiveId}`}>
            <span>К улью</span>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.5577 11.4693L10.0577 5.99982L8.99996 7.06343L13.9652 12.0011L8.99996 16.9387L10.0577 18.0023L15.5577 12.5329C15.6992 12.3921 15.7788 12.2007 15.7788 12.0011C15.7788 11.8014 15.6992 11.61 15.5577 11.4693Z"
                fill={COLORS.ACCENT.DEFAULT}
              />
            </svg>
          </ApiarySchemeModalLink>
        </ApiarySchemeModalItem>
      )}
    </>
  );

  if (isScreenLg) {
    return ReactDOM.createPortal(
      <ApiarySchemeModalWrapper $isOpen={isOpen}>
        <ApiarySchemeModalPopup
          $left={leftMemoized}
          $top={topMemoized}
          ref={popupRef}
          $isOpen={isOpen}
        >
          <Content />
        </ApiarySchemeModalPopup>
      </ApiarySchemeModalWrapper>,
      document.body
    );
  }

  return (
    <Drawer isOpen={isOpen} position="bottom" onClose={onClose} borderRadius={[16, 16, 0, 0]}>
      <ApiarySchemeModalDrawer>
        <Content />
      </ApiarySchemeModalDrawer>
    </Drawer>
  );
};
